import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Container,
    Typography,
    Paper,
    Tabs,
    Tab,
    CircularProgress,
    Alert
} from '@mui/material';
import { styled } from '@mui/material/styles';
import PrintIcon from '@mui/icons-material/Print';
import Button from '@mui/material/Button';
import firebaseConfig from '../config/firebaseConfig';

// Dynamic imports for optional dependencies
let ReactMarkdown = null;
let remarkGfm = null;

try {
    ReactMarkdown = require('react-markdown').default;
    remarkGfm = require('remark-gfm').default;
} catch (err) {
    console.log('react-markdown not installed, using fallback renderer');
}

const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(4),
    marginTop: theme.spacing(3),
    '& h1': {
        color: theme.palette.primary.main,
        borderBottom: `2px solid ${theme.palette.primary.main}`,
        paddingBottom: theme.spacing(1),
        marginBottom: theme.spacing(3),
    },
    '& h2': {
        color: theme.palette.primary.main,
        marginTop: theme.spacing(4),
        marginBottom: theme.spacing(2),
        borderBottom: `1px solid ${theme.palette.divider}`,
        paddingBottom: theme.spacing(1),
    },
    '& h3': {
        color: theme.palette.text.primary,
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(1.5),
        fontWeight: 600,
    },
    '& h4': {
        color: theme.palette.text.secondary,
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(1),
    },
    '& pre': {
        backgroundColor: '#263238',
        color: '#aed581',
        padding: theme.spacing(2),
        borderRadius: theme.shape.borderRadius,
        overflow: 'auto',
        fontSize: '0.95em',
        lineHeight: 1.5,
    },
    '& code': {
        backgroundColor: theme.palette.action.hover,
        padding: '2px 6px',
        borderRadius: 3,
        fontSize: '0.95em',
        fontFamily: 'Courier New, monospace',
    },
    '& pre code': {
        backgroundColor: 'transparent',
        padding: 0,
        color: 'inherit',
        fontSize: '0.95em',
    },
    '& table': {
        width: '100%',
        borderCollapse: 'collapse',
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2),
        '& th': {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            padding: theme.spacing(1.5),
            textAlign: 'left',
            fontWeight: 600,
        },
        '& td': {
            padding: theme.spacing(1.5),
            borderBottom: `1px solid ${theme.palette.divider}`,
        },
        '& tr:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },
    '& ul, & ol': {
        marginLeft: theme.spacing(3),
        marginBottom: theme.spacing(2),
    },
    '& li': {
        marginBottom: theme.spacing(1),
    },
    '& blockquote': {
        borderLeft: `4px solid ${theme.palette.primary.main}`,
        paddingLeft: theme.spacing(2),
        marginLeft: 0,
        fontStyle: 'italic',
        color: theme.palette.text.secondary,
    },
    '& a': {
        color: theme.palette.primary.main,
        textDecoration: 'none',
        '&:hover': {
            textDecoration: 'underline',
        },
    },
    '& img': {
        maxWidth: '100%',
        height: 'auto',
        borderRadius: theme.shape.borderRadius,
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    '@media print': {
        boxShadow: 'none',
        '& pre': {
            border: 'none',
            background: '#f5f5f5',
            pageBreakInside: 'avoid',
            padding: '8px 12px',
            fontSize: '8pt',
            lineHeight: 1.3,
            margin: '8px 0',
        },
        '& code': {
            fontSize: '8pt',
        },
        '& table': {
            pageBreakInside: 'avoid',
        },
        '& h2': {
            pageBreakAfter: 'avoid',
        },
        '& a': {
            color: 'inherit',
            textDecoration: 'none',
            pointerEvents: 'none',
        },
        '& a[href]::after': {
            content: 'none',
        },
        // Hide footer copyright/date (last hr and em elements)
        '& hr:last-of-type': {
            display: 'none',
        },
        '& > em:last-child, & > p:last-child > em': {
            display: 'none',
        },
    },
}));

// Document configuration (static)
const DOCUMENTS = [
    {
        title: 'Quick-Start Anleitung',
        file: '/PUNCHOUT_QUICKSTART.md'
    },
    {
        title: 'Vollständige Anleitung',
        file: '/PUNCHOUT_ANLEITUNG_KUNDE.md'
    }
];

export function PunchoutHelp() {
    const [activeTab, setActiveTab] = useState(0);
    const [markdownContent, setMarkdownContent] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const clientConfig = useMemo(() => {
        if (firebaseConfig.projectId === "litport-schoeningh") {
            return {
                name: 'Schoeningh',
                fullName: 'Schoeningh',
                domain: 'schoeningh-buch.de',
                url: firebaseConfig.siteUrl,
                punchoutUrl: `${firebaseConfig.siteUrl}/app/punchout/initialize`,
                email: firebaseConfig.email?.[0] || 'abo@schoeningh-buch.de',
                cxmlIdentity: 'schoeningh-punchout',
                upperName: 'SCHOENINGH',
                projectId: 'litport-schoeningh'
            };
        }

        if (
            firebaseConfig.projectId === "litport-litpoet" ||
            firebaseConfig.projectId === "litpoet" ||
            window.location.hostname.includes('brunswiker')
        ) {
            return {
                name: 'Brunswiker',
                fullName: 'Litpoet Brunswiker',
                domain: 'brunswiker.de',
                url: 'https://litpoet.brunswiker.de',
                punchoutUrl: 'https://litpoet.brunswiker.de/app/punchout/initialize',
                email: 'support@brunswiker.de',
                cxmlIdentity: 'brunswiker-punchout',
                upperName: 'BRUNSWIKER',
                projectId: 'litport-litpoet'
            };
        }

        return {
            name: 'Heine',
            fullName: 'Heine Fachmedien',
            domain: 'heinefachmedien.de',
            url: 'https://litport2.heinefachmedien.de',
            punchoutUrl: 'https://litport2.heinefachmedien.de/app/punchout/initialize',
            email: 'support@heinefachmedien.de',
            cxmlIdentity: 'heine-punchout',
            upperName: 'HEINE',
            projectId: 'litport-heine-c2471'
        };
    }, []);

    useEffect(() => {
        let isMounted = true;
        
        const loadMarkdown = async () => {
            setLoading(true);
            setError(null);
            
            try {
                const filePath = DOCUMENTS[activeTab].file;
                const response = await fetch(filePath);
                
                if (!response.ok) {
                    throw new Error(`Datei nicht gefunden: ${filePath}`);
                }
                
                let text = await response.text();
                
                // Process markdown content - replace Heine placeholders for non-Heine tenants.
                // IMPORTANT: order matters. Replace the most specific patterns (full URLs,
                // full email, punchout path) BEFORE the bare "heinefachmedien.de" domain,
                // otherwise the generic domain replace destroys the literal those specific
                // rules are trying to match (e.g. "litport2.heinefachmedien.de" would become
                // "litport2.schoeningh-buch.de", keeping the wrong "litport2." host prefix).
                if (clientConfig.name !== 'Heine') {
                    text = text.replace(/https:\/\/litport2\.heinefachmedien\.de/g, clientConfig.url);
                    text = text.replace(/litport2\.heinefachmedien\.de\/app\/punchout\/initialize/g, clientConfig.punchoutUrl);
                    text = text.replace(/litport2\.heinefachmedien\.de/g, clientConfig.url.replace(/^https?:\/\//, ''));
                    text = text.replace(/https:\/\/litport-heine-c2471\.web\.app/g, `https://${clientConfig.projectId}.web.app`);
                    text = text.replace(/litport-heine-c2471/g, clientConfig.projectId);
                    text = text.replace(/support@heinefachmedien\.de/g, clientConfig.email);
                    text = text.replace(/heinefachmedien\.de/g, clientConfig.domain);
                    text = text.replace(/heine-punchout/g, clientConfig.cxmlIdentity);
                    text = text.replace(/HEINE/g, clientConfig.upperName);
                    text = text.replace(/Heine Fachmedien/g, clientConfig.fullName);
                    text = text.replace(/Heine/g, clientConfig.name);
                }
                
                if (isMounted) {
                    setMarkdownContent(text);
                }
            } catch (err) {
                console.error('Error loading markdown:', err);
                if (isMounted) {
                    setError(`Fehler beim Laden der Dokumentation: ${err.message}`);
                }
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };
        
        loadMarkdown();
        
        return () => {
            isMounted = false;
        };
    }, [activeTab, clientConfig]);

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    const handlePrint = () => {
        window.print();
    };

    return (
        <Container maxWidth="lg">
            <Box sx={{ py: 4 }}>
                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center', 
                    mb: 2,
                    '@media print': {
                        display: 'none'
                    }
                }}>
                    <Box>
                        <Typography variant="h3" component="h1" gutterBottom>
                            📚 Punchout Dokumentation
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            Hier finden Sie alle wichtigen Informationen zur Punchout-Integration.
                        </Typography>
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<PrintIcon />}
                        onClick={handlePrint}
                        sx={{ 
                            height: 'fit-content',
                            '@media print': {
                                display: 'none'
                            }
                        }}
                    >
                        Drucken / Als PDF speichern
                    </Button>
                </Box>

                <Paper sx={{ 
                    mt: 3,
                    '@media print': {
                        display: 'none'
                    }
                }}>
                    <Tabs
                        value={activeTab}
                        onChange={handleTabChange}
                        variant="fullWidth"
                        indicatorColor="primary"
                        textColor="primary"
                    >
                        {DOCUMENTS.map((doc, index) => (
                            <Tab
                                key={index}
                                label={doc.title}
                            />
                        ))}
                    </Tabs>
                </Paper>

                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <CircularProgress />
                    </Box>
                )}

                {error && (
                    <Alert severity="error" sx={{ mt: 3 }}>
                        {error}
                    </Alert>
                )}

                {!loading && !error && (
                    <StyledPaper elevation={2}>
                        {/* Print-only title */}
                        <Box sx={{ 
                            display: 'none',
                            '@media print': {
                                display: 'block',
                                mb: 3,
                                pb: 2,
                                borderBottom: '2px solid #1976d2'
                            }
                        }}>
                            <Typography variant="h4" component="h1" color="primary">
                                {DOCUMENTS[activeTab].title}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {clientConfig.fullName} - Punchout Dokumentation
                            </Typography>
                        </Box>
                        
                        {ReactMarkdown ? (
                            <ReactMarkdown
                                remarkPlugins={remarkGfm ? [remarkGfm] : []}
                                components={{
                                    // Custom component for code blocks
                                    code({node, inline, className, children, ...props}) {
                                        return inline ? (
                                            <code className={className} {...props}>
                                                {children}
                                            </code>
                                        ) : (
                                            <pre>
                                                <code className={className} {...props}>
                                                    {children}
                                                </code>
                                            </pre>
                                        );
                                    },
                                    // Open links in new tab
                                    a({node, children, href, ...props}) {
                                        return (
                                            <a
                                                href={href}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                {...props}
                                            >
                                                {children}
                                            </a>
                                        );
                                    },
                                }}
                            >
                                {markdownContent}
                            </ReactMarkdown>
                        ) : (
                            <Box>
                                <Alert severity="info" sx={{ mb: 3 }}>
                                    Markdown-Parser wird geladen... Falls diese Meldung bestehen bleibt, führen Sie bitte aus: <code>npm install react-markdown remark-gfm</code>
                                </Alert>
                                <pre style={{ 
                                    whiteSpace: 'pre-wrap', 
                                    wordWrap: 'break-word',
                                    fontFamily: 'inherit',
                                    fontSize: '0.95em'
                                }}>
                                    {markdownContent}
                                </pre>
                            </Box>
                        )}
                    </StyledPaper>
                )}
            </Box>
        </Container>
    );
}

export default PunchoutHelp;

